import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getCompany, updateCompany, getTaxRegimes } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { selectOnFocus, parseInt2 } from "../lib/inputHelpers";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Building2, Upload, Trash2, FileText, Mail, Hash, MessageSquare, BookOpen, Globe, ImageIcon, UserCircle2, Receipt, Scale, MapPin, Landmark } from "lucide-react";
import { toast } from "sonner";
import AvatarPicker, { InitialsFallback } from "../components/AvatarPicker";
import { MaterialPreferencesSection, EmailSettingsSection } from "../components/settings/UserPreferencesSections";
import CollapsibleSettingsCard from "../components/settings/CollapsibleSettingsCard";
import { TAX_REGIME_HINTS } from "../lib/taxRegimes";

// v6 F10 — Feedback adres (productie). Vervangt het oude testadres.
const FEEDBACK_EMAIL = "emmameasure@gmail.com";

const BRAND_URL = "beta.emmameasure.com";

// v6 F3.5c — Landen + per-land BTW-tarieven en labels van fiscale nummers.
// LU/FR/DE hebben nog geen verified TaxRegime-catalog → de UI toont automatisch
// alleen "Standaard BTW" via /api/tax-regimes (catalog_available=false).
const COUNTRY_OPTIONS = [
  { code: "BE", label: "België",     flag: "🇧🇪",
    vatRates: [0, 6, 21],
    vatLabel: "BTW-nummer",   vatPlaceholder: "BE 0XXX XXX XXX",
    regLabel: null,           regPlaceholder: "",
    postcodePh: "1000",       cityPh: "Brussel",
    hint: "BE: BTW-tarieven 0% / 6% / 21% · BTW-nummer verplicht op offerte.",
    locales: [
      { code: "nl-BE", label: "Nederlands (België)" },
      { code: "fr-BE", label: "Français (Belgique)" },
      { code: "de-BE", label: "Deutsch (Belgien)" },
    ],
  },
  { code: "NL", label: "Nederland",  flag: "🇳🇱",
    vatRates: [0, 9, 21],
    vatLabel: "BTW-id",       vatPlaceholder: "NL XXXXXXXXXB01",
    regLabel: "KvK-nummer",   regPlaceholder: "12345678",
    postcodePh: "1234 AB",    cityPh: "Amsterdam",
    hint: "NL: BTW-tarieven 0% / 9% / 21% · BTW-id + KvK-nummer verplicht op offerte.",
    locales: [{ code: "nl-NL", label: "Nederlands (Nederland)" }],
  },
  { code: "LU", label: "Luxemburg",  flag: "🇱🇺",
    vatRates: [0, 8, 17],
    vatLabel: "VAT-nummer",   vatPlaceholder: "LU XXXXXXXX",
    regLabel: "RCS / registratienummer", regPlaceholder: "B XXXXXX",
    postcodePh: "L-1234",     cityPh: "Luxembourg",
    hint: "LU: Fiscale configuratie nog niet volledig beschikbaar — voorlopig alleen Standaard BTW.",
    locales: [
      { code: "fr-LU", label: "Français (Luxembourg)" },
      { code: "de-LU", label: "Deutsch (Luxemburg)" },
      { code: "lb-LU", label: "Lëtzebuergesch" },
    ],
  },
  { code: "FR", label: "Frankrijk",  flag: "🇫🇷",
    vatRates: [0, 10, 20],
    vatLabel: "TVA intracommunautaire", vatPlaceholder: "FR XX XXXXXXXXX",
    regLabel: "SIREN / SIRET", regPlaceholder: "XXX XXX XXX",
    postcodePh: "75001",       cityPh: "Paris",
    hint: "FR: Fiscale configuratie nog niet volledig beschikbaar — voorlopig alleen Standaard BTW.",
    locales: [{ code: "fr-FR", label: "Français (France)" }],
  },
  { code: "DE", label: "Duitsland",  flag: "🇩🇪",
    vatRates: [0, 7, 19],
    vatLabel: "USt-IdNr.",    vatPlaceholder: "DE XXXXXXXXX",
    regLabel: "Steuernummer", regPlaceholder: "XX/XXX/XXXXX",
    postcodePh: "10115",      cityPh: "Berlin",
    hint: "DE: Fiscale configuratie nog niet volledig beschikbaar — voorlopig alleen Standaard BTW.",
    locales: [{ code: "de-DE", label: "Deutsch (Deutschland)" }],
  },
];

const COUNTRY_BY_CODE = Object.fromEntries(COUNTRY_OPTIONS.map((c) => [c.code, c]));

function openFeedbackMail() {
  const subject = "Feedback EmmaMeasure™";
  const path = (typeof window !== "undefined" && window.location)
    ? `${window.location.pathname}${window.location.search}` : "?";
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) ? navigator.userAgent : "?";
  const version = (typeof process !== "undefined" && process.env?.REACT_APP_VERSION)
    || "EmmaMeasure™";
  const body = [
    "— Beschrijf wat je deed, wat je verwachtte en wat er gebeurde —",
    "",
    "",
    "—",
    `App: EmmaMeasure™ — powered by EmmaForge™`,
    `App-versie: ${version}`,
    `Pagina: ${BRAND_URL}${path}`,
    `Browser/device: ${ua}`,
    `Tijdstip: ${new Date().toISOString()}`,
  ].join("\n");
  const href = `mailto:${FEEDBACK_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  window.location.href = href;
}

const empty = {
  name: "",
  country: "BE",
  address_line1: "", address_line2: "", postal_code: "", city: "",
  address: "", // legacy fallback
  vat_number: "", kvk_number: "",
  // v6 F3.5c — Generiek secundair registratienummer (RCS/SIREN/Steuernummer/…).
  company_registration_number: "",
  email: "", phone: "", logo_data_url: "",
  quote_number_prefix: "OFF", quote_number_include_year: true,
  quote_number_sequence: 1, quote_number_reset_yearly: true,
  // v6 F-Invoicing F3 — Facturatie-defaults + i18n.
  invoice_number_prefix: "FAC", invoice_number_include_year: true,
  invoice_number_sequence: 1, invoice_number_reset_yearly: true,
  // v14u Sprint 2A — Aparte, onafhankelijke sequences per doc_kind.
  credit_note_number_prefix: "CN", credit_note_number_include_year: true,
  credit_note_number_sequence: 1, credit_note_number_reset_yearly: true,
  pro_forma_number_prefix: "PF", pro_forma_number_include_year: true,
  pro_forma_number_sequence: 1, pro_forma_number_reset_yearly: true,
  iban: "", bic: "",
  payment_reference_template: "Factuur {invoice_number}",
  default_invoice_due_days: 30,
  locale: "nl-BE", currency: "EUR",
  default_validity_days: 30,
  default_payment_terms: "",
  default_email_message: "",
  show_signature_block_default: true,
  // v6 F3.5b — Standaard percentages voor deelfacturatie.
  default_deposit_pct: 30,
  default_interim_pct: 40,
  default_final_pct: 30,
  // v6 F3.5c pre-deploy — Aparte factuur-betalingsvoorwaarden (los van offerte).
  default_invoice_payment_terms: "",
};

export default function CompanySettings() {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  // v14am — AutoMeasure toggle AutoSave state. Onafhankelijk van globale save.
  const [autoMeasureSaving, setAutoMeasureSaving] = useState(false);
  // v14as — Finance toggle AutoSave state + resolved status message list.
  const [financeSaving, setFinanceSaving] = useState(false);
  const [financeStatus, setFinanceStatus] = useState(null);
  const fileRef = useRef(null);
  // v6 F13 §3 — AvatarPicker Dialog: pas geopend als de gebruiker klikt op
  // "Standaardavatar kiezen". Standaard is er GEEN grote iconengrid meer op
  // de pagina, alleen 3 duidelijke keuzes + preview.
  const [avatarPickerOpen, setAvatarPickerOpen] = useState(false);
  // v6 F3.5b IA v2 — Country-aware BTW-regimes state.
  const [taxRegimes, setTaxRegimes] = useState({ regimes: [], catalog_available: true, country: "BE" });

  useEffect(() => {
    getCompany().then((c) => setForm({ ...empty, ...c, country: c.country || "BE" })).catch(() => {});
  }, []);

  // v14as — Finance status refresht op mount + na company-load zodra we
  // weten of Finance ingeschakeld is. Bij Finance-off hoeft er niets geladen
  // te worden — de status-endpoint retourneert dan `enabled=False`.
  useEffect(() => {
    if (form.finance_enabled) loadFinanceStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.finance_enabled]);

  // v6 F3.5b IA v2 — Fetch regimes voor (country, locale). Refetcht bij wijziging.
  useEffect(() => {
    if (!form.country) return;
    getTaxRegimes(form.country, form.locale || "nl-BE")
      .then(setTaxRegimes)
      .catch(() => setTaxRegimes({ regimes: [{ key: "standard", label: "Standaard BTW", legal_notice_status: "verified" }], catalog_available: false, country: form.country }));
  }, [form.country, form.locale]);

  const country = COUNTRY_BY_CODE[form.country] || COUNTRY_BY_CODE.BE;

  const onLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setForm((s) => ({ ...s, logo_data_url: reader.result }));
    reader.readAsDataURL(f);
  };

  const save = async () => {
    setSaving(true);
    try {
      // v6 F3.5c — KvK is NL-specifiek: wissen buiten NL zodat we geen
      // inconsistente data behouden na een country-wissel. Het generieke
      // `company_registration_number` wordt niet gewist (mag leeg staan).
      const payload = { ...form };
      if (payload.country !== "NL") payload.kvk_number = "";
      await updateCompany(payload);
      toast.success("Bedrijfsgegevens opgeslagen");
    } catch { toast.error("Fout bij opslaan"); }
    finally { setSaving(false); }
  };

  // v14am — AutoMeasure toggle AutoSave. Optimistic UI met rollback bij fout.
  // De backend blijft de source-of-truth; wanneer persistentie faalt tonen
  // we AutoMeasure NIET als "aan" (rollback naar vorige waarde).
  const toggleAutoMeasure = async (nextVal) => {
    const previous = !!form.automeasure_enabled;
    setForm((s) => ({ ...s, automeasure_enabled: nextVal }));
    setAutoMeasureSaving(true);
    try {
      const payload = { ...form, automeasure_enabled: nextVal };
      if (payload.country !== "NL") payload.kvk_number = "";
      await updateCompany(payload);
      toast.success(nextVal ? "AutoMeasure™ ingeschakeld" : "AutoMeasure™ uitgeschakeld");
    } catch (e) {
      // Rollback — nooit als "aan" tonen wanneer backend het niet accepteerde.
      setForm((s) => ({ ...s, automeasure_enabled: previous }));
      toast.error("AutoMeasure-instelling kon niet opgeslagen worden. Probeer opnieuw.");
    } finally {
      setAutoMeasureSaving(false);
    }
  };

  // v14as — Finance master toggle AutoSave (optimistic + rollback on error).
  // Never persists automatically when Finance is disabled; a failure at
  // enable-time rolls back so the tenant is not left in a half-on state.
  const toggleFinance = async (nextVal) => {
    const previous = !!form.finance_enabled;
    setForm((s) => ({ ...s, finance_enabled: nextVal }));
    setFinanceSaving(true);
    try {
      const API_BASE = process.env.REACT_APP_BACKEND_URL;
      const r = await fetch(`${API_BASE}/api/finance/company/settings`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ finance_enabled: nextVal }),
      });
      if (!r.ok) throw new Error("Server error");
      toast.success(nextVal ? "EmmaMeasure Finance ingeschakeld" : "EmmaMeasure Finance uitgeschakeld");
      // Refresh status so any provider-status message (e.g. existing Peppol) toont.
      loadFinanceStatus();
    } catch (e) {
      setForm((s) => ({ ...s, finance_enabled: previous }));
      toast.error("Finance-instelling kon niet opgeslagen worden. Probeer opnieuw.");
    } finally {
      setFinanceSaving(false);
    }
  };

  const loadFinanceStatus = async () => {
    try {
      const API_BASE = process.env.REACT_APP_BACKEND_URL;
      const r = await fetch(`${API_BASE}/api/finance/company/status`, { credentials: "include" });
      if (r.ok) setFinanceStatus(await r.json());
    } catch { /* silent — Finance is optional */ }
  };

  const numberPreview = useMemo(() => {
    const parts = [form.quote_number_prefix || "OFF"];
    if (form.quote_number_include_year) parts.push(String(new Date().getFullYear()));
    parts.push(String(form.quote_number_sequence || 1).padStart(3, "0"));
    return parts.join("-");
  }, [form.quote_number_prefix, form.quote_number_include_year, form.quote_number_sequence]);

  // v6 F3.5b IA v2 — Factuurnummer preview (identieke logica als offerte).
  const invoiceNumberPreview = useMemo(() => {
    const parts = [form.invoice_number_prefix || "FAC"];
    if (form.invoice_number_include_year) parts.push(String(new Date().getFullYear()));
    parts.push(String(form.invoice_number_sequence || 1).padStart(3, "0"));
    return parts.join("-");
  }, [form.invoice_number_prefix, form.invoice_number_include_year, form.invoice_number_sequence]);

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#5D7259]">
          <Building2 className="inline w-3 h-3 mr-1" /> Bedrijfsinstellingen
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#2C2C2A] mt-2 leading-tight">Jouw bedrijf</h1>
        <p className="text-[#4F4F4D] mt-1">Deze gegevens verschijnen bovenaan elke offerte-PDF.</p>
      </div>

      {/* Bedrijfsgegevens */}
      <CollapsibleSettingsCard
        id="company"
        title="Jouw bedrijf"
        icon={<Building2 className="w-4 h-4 text-[#D96C4A]" />}
        defaultOpen={true}
        testid="section-company"
      >
        {/* v6 F13 §3 — Rustige logo/avatar-sectie: preview + 3 primaire keuzes.
            Geen permanente 10-avatar grid meer; de picker opent in een Dialog
            wanneer de gebruiker klikt op "Standaardavatar kiezen". */}
        <div className="flex items-start gap-4 sm:gap-5">
          <div
            className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-[#F0EFEA] border border-[#E5E2DC] flex items-center justify-center overflow-hidden flex-shrink-0"
            data-testid="logo-preview"
          >
            {form.logo_data_url ? (
              <img src={form.logo_data_url} alt="logo" className="w-full h-full object-contain" />
            ) : form.name ? (
              <InitialsFallback name={form.name} />
            ) : (
              <Building2 className="w-8 h-8 text-[#4F4F4D]" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <Label>Bedrijfslogo</Label>
            <p className="text-xs text-[#4F4F4D] mt-1 leading-snug">
              Kies hoe je bedrijfsidentiteit bovenaan de offerte-PDF verschijnt.
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              <Button
                onClick={() => fileRef.current?.click()}
                variant="outline"
                className="h-10 rounded-xl gap-2 text-sm"
                data-testid="logo-upload-btn"
              >
                <Upload className="w-4 h-4" /> Eigen logo uploaden
              </Button>
              <Button
                onClick={() => setAvatarPickerOpen(true)}
                variant="outline"
                className="h-10 rounded-xl gap-2 text-sm"
                data-testid="logo-choose-avatar-btn"
              >
                <ImageIcon className="w-4 h-4" /> Standaardavatar kiezen
              </Button>
              <Button
                onClick={() => setForm({ ...form, logo_data_url: "" })}
                variant="outline"
                className={`h-10 rounded-xl gap-2 text-sm ${!form.logo_data_url ? "border-[#D96C4A] text-[#D96C4A]" : ""}`}
                data-testid="logo-initials-btn"
              >
                <UserCircle2 className="w-4 h-4" /> Alleen initialen
              </Button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="logo-input" />
            </div>
          </div>
        </div>

        {/* v6 F13 §3 — Dialog met de picker; opent enkel op expliciete keuze */}
        <Dialog open={avatarPickerOpen} onOpenChange={setAvatarPickerOpen}>
          <DialogContent className="max-w-lg" data-testid="avatar-picker-dialog">
            <DialogHeader>
              <DialogTitle>Kies een standaardavatar</DialogTitle>
              <DialogDescription>
                Geen eigen logo? Kies een van de EmmaMeasure schildersiconen.
                Je kan ook later een echte logo uploaden.
              </DialogDescription>
            </DialogHeader>
            <AvatarPicker
              currentDataUrl={form.logo_data_url}
              initials={(form.name || "").split(/\s+/).filter(Boolean).slice(0,2).map(s => s[0].toUpperCase()).join("")}
              onSelect={(dataUrl) => {
                setForm((s) => ({ ...s, logo_data_url: dataUrl }));
                setAvatarPickerOpen(false);
              }}
              onClear={() => {
                setForm((s) => ({ ...s, logo_data_url: "" }));
                setAvatarPickerOpen(false);
              }}
            />
          </DialogContent>
        </Dialog>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Label>Bedrijfsnaam *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="h-14 mt-1.5 rounded-xl" data-testid="company-name-input" />
          </div>

          {/* v6 F3.5c — Land (BE/NL/LU/FR/DE). Stuurt BTW-labels + placeholders +
              beschikbare locales. LU/FR/DE hebben nog geen verified TaxRegime. */}
          <div className="sm:col-span-2">
            <Label className="flex items-center gap-1.5">
              <Globe className="w-3.5 h-3.5 text-[#5D7259]" /> Land
            </Label>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-1.5" data-testid="country-toggle">
              {COUNTRY_OPTIONS.map((c) => (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => {
                    // v6 F3.5c — Bij country-wissel: forceer locale naar de eerste
                    // beschikbare locale van het nieuwe land zodat we nooit een
                    // ongeldige combinatie (bv. country=FR + locale=nl-BE) krijgen.
                    const next = COUNTRY_OPTIONS.find((x) => x.code === c.code);
                    const firstLocale = next?.locales?.[0]?.code || "nl-BE";
                    setForm({ ...form, country: c.code, locale: firstLocale });
                  }}
                  className={`h-12 rounded-xl border-2 font-medium transition ${form.country === c.code ? "border-[#D96C4A] bg-[#FFF8F2] text-[#2C2C2A]" : "border-[#E5E2DC] hover:border-[#D96C4A]/40 text-[#4F4F4D]"}`}
                  data-testid={`country-option-${c.code}`}
                >
                  <span className="text-lg mr-1">{c.flag}</span>
                  {c.label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-[#5D7259] mt-1.5">
              {country.hint}
            </p>
          </div>

          {/* v6 F10 — Uitgebreid adres (3 regels) */}
          <div className="sm:col-span-2">
            <Label>Adresregel 1</Label>
            <Input value={form.address_line1} onChange={(e) => setForm({ ...form, address_line1: e.target.value })}
              className="h-14 mt-1.5 rounded-xl" placeholder="Straat en huisnummer" data-testid="company-address1-input" />
          </div>
          <div className="sm:col-span-2">
            <Label>Adresregel 2 <span className="text-[11px] text-[#5D7259] font-normal">(optioneel — bus, verdieping, …)</span></Label>
            <Input value={form.address_line2} onChange={(e) => setForm({ ...form, address_line2: e.target.value })}
              className="h-14 mt-1.5 rounded-xl" placeholder="Bus / verdieping" data-testid="company-address2-input" />
          </div>
          <div>
            <Label>Postcode</Label>
            <Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
              className="h-14 mt-1.5 rounded-xl" placeholder={country.postcodePh} data-testid="company-postalcode-input" />
          </div>
          <div>
            <Label>Stad / gemeente</Label>
            <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
              className="h-14 mt-1.5 rounded-xl" placeholder={country.cityPh} data-testid="company-city-input" />
          </div>

          {/* v6 F3.5c — Fiscale nummers: primair vat_number (label per land) +
              secundair registratienummer (KvK voor NL, generic voor LU/FR/DE). */}
          <div>
            <Label>{country.vatLabel}</Label>
            <Input value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })}
              className="h-14 mt-1.5 rounded-xl font-mono" placeholder={country.vatPlaceholder} data-testid="company-vat-input" />
          </div>
          {form.country === "NL" ? (
            <div>
              <Label>KvK-nummer</Label>
              <Input value={form.kvk_number} onChange={(e) => setForm({ ...form, kvk_number: e.target.value })}
                className="h-14 mt-1.5 rounded-xl font-mono" placeholder="12345678" data-testid="company-kvk-input" />
            </div>
          ) : country.regLabel ? (
            <div>
              <Label>{country.regLabel}</Label>
              <Input value={form.company_registration_number}
                onChange={(e) => setForm({ ...form, company_registration_number: e.target.value })}
                className="h-14 mt-1.5 rounded-xl font-mono" placeholder={country.regPlaceholder}
                data-testid="company-reg-number-input" />
            </div>
          ) : (
            <div>
              <Label>Telefoon</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="h-14 mt-1.5 rounded-xl" data-testid="company-phone-input" />
            </div>
          )}
          {(form.country === "NL" || country.regLabel) && (
            <div className="sm:col-span-2">
              <Label>Telefoon</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="h-14 mt-1.5 rounded-xl" data-testid="company-phone-input" />
            </div>
          )}
          <div className="sm:col-span-2">
            <Label>E-mail</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="h-14 mt-1.5 rounded-xl" data-testid="company-email-input" />
          </div>

          {/* v6 F3.5b — Documenttaal & valuta verhuisd naar "Jouw bedrijf".
              Één centrale bron voor offerte / factuur / creditnota. */}
          <div>
            <Label htmlFor="cs-locale">Documenttaal & regio</Label>
            <select id="cs-locale" value={form.locale || (country.locales?.[0]?.code || "nl-BE")}
              onChange={(e) => setForm({ ...form, locale: e.target.value })}
              className="mt-1.5 flex h-14 w-full rounded-xl border border-[#E5E2DC] bg-white px-3 text-sm"
              data-testid="cs-locale">
              {(country.locales || []).map((l) => (
                <option key={l.code} value={l.code}>{l.label}</option>
              ))}
            </select>
            <p className="text-[11px] text-[#A8A7A0] mt-1">Stuurt PDF-labels, datum- en getalnotatie.</p>
          </div>
          <div>
            <Label htmlFor="cs-currency">Valuta</Label>
            <select id="cs-currency" value={form.currency || "EUR"}
              onChange={(e) => setForm({ ...form, currency: e.target.value })}
              className="mt-1.5 flex h-14 w-full rounded-xl border border-[#E5E2DC] bg-white px-3 text-sm"
              data-testid="cs-currency">
              <option value="EUR">EUR — €</option>
              <option value="USD">USD — $</option>
              <option value="GBP">GBP — £</option>
              <option value="CHF">CHF</option>
            </select>
          </div>
        </div>
      </CollapsibleSettingsCard>

      {/* v6 F3.5b — Offertenummering + Offerte-defaults zijn nu samengevoegd
          in de "Offertes"-sectie hieronder.
          De aparte "Documenten & huisstijl"-kaart is verwijderd:
          documenttaal, valuta en logo/avatar wonen nu uitsluitend in "Jouw bedrijf". */}

      {/* v6 F3.5b — Sectie 2: Offertes (nummering + defaults samengevoegd) */}
      <CollapsibleSettingsCard
        id="quotes"
        title="Offertes"
        icon={<FileText className="w-4 h-4 text-[#D96C4A]" />}
        testid="quotes-card"
      >
        <div className="space-y-5">
          <div>
            <div className="text-[11px] tracking-[0.18em] uppercase text-[#5D7259] font-semibold mb-2">Offertenummering</div>
            <p className="text-xs text-[#4F4F4D] mb-2">Volgende offerte krijgt automatisch dit nummer:&nbsp;
              <strong className="text-[#2C2C2A] font-mono" data-testid="numbering-preview">{numberPreview}</strong>
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Voorvoegsel</Label>
                <Input value={form.quote_number_prefix} onChange={(e) => setForm({ ...form, quote_number_prefix: e.target.value.toUpperCase() })}
                  className="h-12 mt-1.5 rounded-xl font-mono" placeholder="OFF / PM / EM" data-testid="numbering-prefix" />
              </div>
              <div>
                <Label>Volgend nummer</Label>
                <Input type="text" inputMode="numeric" pattern="[0-9]*" value={form.quote_number_sequence}
                  onFocus={selectOnFocus}
                  onChange={(e) => setForm({ ...form, quote_number_sequence: Math.max(1, parseInt2(e.target.value, 1)) })}
                  className="h-12 mt-1.5 rounded-xl font-mono" data-testid="numbering-sequence" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              <div className="flex items-center justify-between border border-[#E5E2DC] rounded-xl px-4 h-12">
                <Label className="cursor-pointer">Jaartal in nummer</Label>
                <Switch checked={!!form.quote_number_include_year}
                  onCheckedChange={(v) => setForm({ ...form, quote_number_include_year: v })}
                  data-testid="numbering-include-year" />
              </div>
              <div className="flex items-center justify-between border border-[#E5E2DC] rounded-xl px-4 h-12">
                <Label className="cursor-pointer">Jaarlijks resetten</Label>
                <Switch checked={!!form.quote_number_reset_yearly}
                  onCheckedChange={(v) => setForm({ ...form, quote_number_reset_yearly: v })}
                  data-testid="numbering-reset-yearly" />
              </div>
            </div>
          </div>

          <div className="border-t border-[#E5E2DC] pt-4">
            <div className="text-[11px] tracking-[0.18em] uppercase text-[#5D7259] font-semibold mb-2">Offerte-defaults</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Geldigheid (dagen)</Label>
                <Input type="text" inputMode="numeric" pattern="[0-9]*" value={form.default_validity_days}
                  onFocus={selectOnFocus}
                  onChange={(e) => setForm({ ...form, default_validity_days: Math.max(1, parseInt2(e.target.value, 30)) })}
                  className="h-12 mt-1.5 rounded-xl" data-testid="default-validity-days" />
              </div>
              <div className="flex items-center justify-between border border-[#E5E2DC] rounded-xl px-4 h-12 sm:mt-7">
                <Label className="cursor-pointer">"Voor akkoord"-blok in PDF</Label>
                <Switch checked={!!form.show_signature_block_default}
                  onCheckedChange={(v) => setForm({ ...form, show_signature_block_default: v })}
                  data-testid="default-signature-block" />
              </div>
            </div>
            <div className="mt-3">
              <Label>Standaard offertevoorwaarden</Label>
              <Textarea value={form.default_payment_terms} rows={3}
                onChange={(e) => setForm({ ...form, default_payment_terms: e.target.value })}
                className="mt-1.5 rounded-xl" data-testid="default-payment-terms" />
              <p className="text-[11px] text-[#A8A7A0] mt-1">Verschijnt onderaan de offerte-PDF. Voor factuurvoorwaarden zie 'Facturen &amp; betalingen'.</p>
            </div>
          </div>
        </div>
      </CollapsibleSettingsCard>

      {/* v6 F3.5b IA v2 — Sectie 4: Facturen & betalingen.
          Volgorde: Factuurnummering → Betalingsgegevens → Standaard deelfacturatie.
          Zelfde visuele logica als Offertes → Offertenummering. */}
      <CollapsibleSettingsCard
        id="invoicing"
        title="Facturen & betalingen"
        icon={<Receipt className="w-4 h-4 text-[#D96C4A]" />}
        testid="invoicing-card"
      >
        <div className="space-y-5">
          {/* Sub-blok 1 — FACTUURNUMMERING */}
          <div>
            <div className="text-[11px] tracking-[0.18em] uppercase text-[#5D7259] font-semibold mb-2">Factuurnummering</div>
            <p className="text-xs text-[#4F4F4D] mb-2">Volgende factuur krijgt automatisch dit nummer:&nbsp;
              <strong className="text-[#2C2C2A] font-mono" data-testid="invoice-numbering-preview">{invoiceNumberPreview}</strong>
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Voorvoegsel factuur</Label>
                <Input value={form.invoice_number_prefix || "FAC"}
                  onChange={(e) => setForm({ ...form, invoice_number_prefix: e.target.value.toUpperCase() })}
                  className="h-12 mt-1.5 rounded-xl font-mono" placeholder="FAC" data-testid="cs-invoice-prefix" />
              </div>
              <div>
                <Label>Volgend factuurnummer</Label>
                <Input type="text" inputMode="numeric" pattern="[0-9]*"
                  value={form.invoice_number_sequence ?? 1}
                  onFocus={selectOnFocus}
                  onChange={(e) => setForm({ ...form, invoice_number_sequence: Math.max(1, parseInt2(e.target.value, 1)) })}
                  className="h-12 mt-1.5 rounded-xl font-mono" data-testid="cs-invoice-sequence" />
              </div>
            </div>
            <p className="text-[11px] text-[#4F4F4D] mt-2 leading-snug">
              Pas dit aan om de telling te resetten of door te tellen vanaf een bepaald nummer — handig als je EmmaMeasure midden in het jaar begint te gebruiken.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              <div className="flex items-center justify-between border border-[#E5E2DC] rounded-xl px-4 h-12">
                <Label className="cursor-pointer">Jaartal in nummer</Label>
                <Switch checked={!!form.invoice_number_include_year}
                  onCheckedChange={(v) => setForm({ ...form, invoice_number_include_year: v })}
                  data-testid="cs-invoice-include-year" />
              </div>
              <div className="flex items-center justify-between border border-[#E5E2DC] rounded-xl px-4 h-12">
                <Label className="cursor-pointer">Jaarlijks resetten</Label>
                <Switch checked={!!form.invoice_number_reset_yearly}
                  onCheckedChange={(v) => setForm({ ...form, invoice_number_reset_yearly: v })}
                  data-testid="cs-invoice-reset-yearly" />
              </div>
            </div>

            {/* v14u Sprint 2A — Creditnota (aparte, onafhankelijke sequence) */}
            <div className="mt-6 pt-4 border-t border-[#E5E2DC]">
              <div className="text-[11px] tracking-[0.18em] uppercase text-[#5D7259] font-semibold mb-2">Creditnota-nummering</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Voorvoegsel creditnota</Label>
                  <Input value={form.credit_note_number_prefix || "CN"}
                    onChange={(e) => setForm({ ...form, credit_note_number_prefix: e.target.value.toUpperCase() })}
                    className="h-12 mt-1.5 rounded-xl font-mono" placeholder="CN" data-testid="cs-credit-note-prefix" />
                </div>
                <div>
                  <Label>Volgend creditnotanummer</Label>
                  <Input type="text" inputMode="numeric" pattern="[0-9]*"
                    value={form.credit_note_number_sequence ?? 1}
                    onFocus={selectOnFocus}
                    onChange={(e) => setForm({ ...form, credit_note_number_sequence: Math.max(1, parseInt2(e.target.value, 1)) })}
                    className="h-12 mt-1.5 rounded-xl font-mono" data-testid="cs-credit-note-sequence" />
                </div>
              </div>
            </div>

            {/* v14u Sprint 2A — Pro forma (aparte, onafhankelijke sequence) */}
            <div className="mt-6 pt-4 border-t border-[#E5E2DC]">
              <div className="text-[11px] tracking-[0.18em] uppercase text-[#5D7259] font-semibold mb-2">Pro-forma-nummering</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Voorvoegsel pro forma</Label>
                  <Input value={form.pro_forma_number_prefix || "PF"}
                    onChange={(e) => setForm({ ...form, pro_forma_number_prefix: e.target.value.toUpperCase() })}
                    className="h-12 mt-1.5 rounded-xl font-mono" placeholder="PF" data-testid="cs-pro-forma-prefix" />
                </div>
                <div>
                  <Label>Volgend pro-formanummer</Label>
                  <Input type="text" inputMode="numeric" pattern="[0-9]*"
                    value={form.pro_forma_number_sequence ?? 1}
                    onFocus={selectOnFocus}
                    onChange={(e) => setForm({ ...form, pro_forma_number_sequence: Math.max(1, parseInt2(e.target.value, 1)) })}
                    className="h-12 mt-1.5 rounded-xl font-mono" data-testid="cs-pro-forma-sequence" />
                </div>
              </div>
              <p className="text-[11px] text-[#4F4F4D] mt-2 leading-snug italic">
                Creditnota- en pro-formatellers zijn volledig onafhankelijk van de factuurteller.
              </p>
            </div>
          </div>

          {/* Sub-blok 2 — BETALINGSGEGEVENS */}
          <div className="border-t border-[#E5E2DC] pt-4">
            <div className="text-[11px] tracking-[0.18em] uppercase text-[#5D7259] font-semibold mb-2">Betalingsgegevens</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="cs-iban">IBAN</Label>
                <Input id="cs-iban" value={form.iban || ""}
                  onChange={(e) => setForm({ ...form, iban: e.target.value })}
                  placeholder="BE68 5390 0754 7034"
                  className="h-12 mt-1.5 rounded-xl" data-testid="cs-iban" />
              </div>
              <div>
                <Label htmlFor="cs-bic">BIC</Label>
                <Input id="cs-bic" value={form.bic || ""}
                  onChange={(e) => setForm({ ...form, bic: e.target.value })}
                  placeholder="GKCCBEBB"
                  className="h-12 mt-1.5 rounded-xl" data-testid="cs-bic" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
              <div>
                <Label htmlFor="cs-inv-due">Standaard vervaltermijn (dagen)</Label>
                <Input id="cs-inv-due" type="number" min="1" max="365"
                  value={form.default_invoice_due_days ?? 30}
                  onChange={(e) => setForm({ ...form, default_invoice_due_days: parseInt(e.target.value, 10) || 30 })}
                  className="h-12 mt-1.5 rounded-xl" data-testid="cs-invoice-due-days" />
                <p className="text-[11px] text-[#A8A7A0] mt-1">Berekent de vervaldatum bij het definitief maken van een factuur.</p>
              </div>
              <div>
                <Label htmlFor="cs-inv-ref">Betalingsreferentie-sjabloon</Label>
                <Input id="cs-inv-ref" value={form.payment_reference_template || ""}
                  onChange={(e) => setForm({ ...form, payment_reference_template: e.target.value })}
                  placeholder="Factuur {invoice_number}"
                  className="h-12 mt-1.5 rounded-xl" data-testid="cs-payment-reference-template" />
                <p className="text-[11px] text-[#A8A7A0] mt-1">Gebruik <code className="text-[10px]">{"{invoice_number}"}</code> als placeholder.</p>
              </div>
            </div>
            {/* v6 F3.5c pre-deploy — Aparte factuurvoorwaarden. Wordt onder
                de vervaldatum-regel getoond op de factuur-PDF. Los van de
                offertevoorwaarden zodat 14/30 dagen elkaar nooit tegenspreken. */}
            <div className="mt-3">
              <Label htmlFor="cs-inv-payment-terms">Standaard betalingsvoorwaarden factuur</Label>
              <Textarea id="cs-inv-payment-terms" value={form.default_invoice_payment_terms || ""}
                onChange={(e) => setForm({ ...form, default_invoice_payment_terms: e.target.value })}
                rows={3}
                placeholder="Bijv. Betaling binnen 30 dagen na factuurdatum."
                className="mt-1.5 rounded-xl" data-testid="cs-default-invoice-payment-terms" />
              <p className="text-[11px] text-[#A8A7A0] mt-1">
                Verschijnt onder de vervaldatum in het betaalblok van elke factuur-PDF. Laat leeg om geen extra tekst te tonen.
              </p>
            </div>
          </div>

          {/* Sub-blok 3 — STANDAARD DEELFACTURATIE
              HARDE REGEL: eindfactuur clamped altijd op resterend saldo, ongeacht %. */}
          <div className="border-t border-[#E5E2DC] pt-4">
            <div className="text-[11px] tracking-[0.18em] uppercase text-[#5D7259] font-semibold mb-2">Standaard deelfacturatie</div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label htmlFor="cs-dep">Voorschot %</Label>
                <Input id="cs-dep" type="number" min="0" max="100" step="1"
                  value={form.default_deposit_pct ?? 30}
                  onChange={(e) => setForm({ ...form, default_deposit_pct: Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)) })}
                  className="h-12 mt-1.5 rounded-xl" data-testid="cs-default-deposit-pct" />
              </div>
              <div>
                <Label htmlFor="cs-int">Tussenfactuur %</Label>
                <Input id="cs-int" type="number" min="0" max="100" step="1"
                  value={form.default_interim_pct ?? 40}
                  onChange={(e) => setForm({ ...form, default_interim_pct: Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)) })}
                  className="h-12 mt-1.5 rounded-xl" data-testid="cs-default-interim-pct" />
              </div>
              <div>
                <Label htmlFor="cs-fin">Eindfactuur %</Label>
                <Input id="cs-fin" type="number" min="0" max="100" step="1"
                  value={form.default_final_pct ?? 30}
                  onChange={(e) => setForm({ ...form, default_final_pct: Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)) })}
                  className="h-12 mt-1.5 rounded-xl" data-testid="cs-default-final-pct" />
              </div>
            </div>
            <p className="text-[11px] text-[#6E6E6C] mt-2 leading-snug">
              Deze percentages worden als voorstel ingevuld bij het maken van een factuur. Je kunt per factuur altijd een ander percentage of bedrag kiezen en meerdere tussenfacturen maken. De eindfactuur neemt automatisch het resterend saldo.
            </p>
          </div>
        </div>
      </CollapsibleSettingsCard>

      {/* v14as — EmmaMeasure Finance (F1 Foundation). Master toggle direct
          onder "Facturen & betalingen". v14at: AutoMeasure verplaatst naar
          onder "E-mail" — Finance schuift dus visueel omhoog. Default OFF.
          Diepere sub-instellingen (e-invoicing modus, bank, matching)
          worden in F3-F7 toegevoegd; F1 laat een
          tenant enkel Finance zelf aan/uit zetten. */}
      <CollapsibleSettingsCard
        id="finance"
        title="EmmaMeasure Finance (early access)"
        icon={<Landmark className="w-4 h-4 text-[#D96C4A]" />}
        testid="finance-card"
      >
        <div className="space-y-3">
          <p className="text-sm text-[#4F4F4D]">
            EmmaMeasure Finance is een <strong>optionele</strong> financiële laag
            op EmmaMeasure. Wanneer ingeschakeld, kan u later elektronische
            facturen versturen/ontvangen (Peppol), inkomende leveranciersfacturen
            beheren, een bankverbinding leggen en betalingen automatisch matchen.
            Deze fase (F1) activeert alleen de basis; verdere onderdelen komen
            gefaseerd beschikbaar na goedkeuring. EmmaMeasure blijft zonder Finance
            volledig werken zoals voorheen.
          </p>
          <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="finance-toggle-wrapper">
            <input
              type="checkbox"
              className="w-5 h-5 accent-[#D96C4A]"
              checked={!!form.finance_enabled}
              disabled={financeSaving}
              onChange={(e) => toggleFinance(e.target.checked)}
              data-testid="finance-toggle"
            />
            <span className="text-sm font-medium text-[#2C2C2A]">
              EmmaMeasure Finance inschakelen
            </span>
            {financeSaving && (
              <span className="text-[11px] text-[#A8A7A0] italic" data-testid="finance-saving">Opslaan…</span>
            )}
          </label>
          <p className="text-[11px] text-[#A8A7A0]">
            {form.finance_enabled
              ? "Ingeschakeld — de Finance-onderdelen die klaar zijn worden zichtbaar in het menu. Onderdelen in voorbereiding tonen “binnenkort beschikbaar”."
              : "Uitgeschakeld — Finance-menu en Finance-routes zijn inactief. Bestaande facturatie/PDF-flows blijven ongewijzigd."}
          </p>

          {form.finance_enabled && financeStatus?.messages?.length > 0 && (
            <div className="space-y-2" data-testid="finance-messages">
              {financeStatus.messages.map((m, i) => (
                <div
                  key={i}
                  className="rounded-lg bg-[#F2F5EE] border border-[#6B8E6B]/40 p-3 text-[12px]"
                  data-testid={`finance-message-${m.code}`}
                >
                  <div className="font-semibold text-[#2C2C2A]">{m.title}</div>
                  <div className="text-[#4F4F4D] mt-1 leading-snug">{m.body}</div>
                  {m.actions?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {m.actions.map((a) => (
                        <span
                          key={a.key}
                          className="inline-flex items-center px-2 py-1 rounded border border-[#6B8E6B]/50 bg-white text-[11px] text-[#3F5E3F]"
                          data-testid={`finance-action-${a.key}`}
                        >
                          {a.label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="rounded-lg bg-[#FAF9F6] border border-[#E5E2DC] p-3 text-[11px] text-[#4F4F4D] space-y-1">
            <div><strong>Beschikbaar in deze fase (F1 + F2):</strong></div>
            <div>· Feature aan/uit</div>
            <div>· UBL 2.1 (Peppol BIS Billing 3.0) generatie voor bestaande facturen (preview + validatie)</div>
            <div className="pt-1 opacity-70">
              In voorbereiding: elektronische verzending &amp; ontvangst, bankverbinding, automatische betalingsmatching. Elke fase vereist expliciete goedkeuring.
            </div>
          </div>
        </div>
      </CollapsibleSettingsCard>

      {/* v6 F3.5b IA v2 — Sectie 5: BTW & fiscale regels (country-aware, dynamisch).
          Toont regimes uit de backend-catalog voor (country, locale). Landen
          zonder verified catalog krijgen een safe-standard fallback + banner. */}
      <CollapsibleSettingsCard
        id="tax-regimes"
        title="BTW & fiscale regels"
        icon={<Scale className="w-4 h-4 text-[#D96C4A]" />}
        testid="tax-regimes-card"
      >
        <p className="text-xs text-[#4F4F4D]">
          BTW-regimes beschikbaar per klant en per factuur voor land <strong>{taxRegimes.country || form.country || "BE"}</strong>.
        </p>

        {!taxRegimes.catalog_available && (
          <div className="mt-3 p-3 rounded-xl bg-[#FDF7F2] border border-[#F0C4A3] text-xs text-[#7F4124]" data-testid="tax-regimes-unsupported-banner">
            <strong>Fiscale configuratie voor {taxRegimes.country || form.country} nog niet beschikbaar.</strong>
            <p className="mt-1 leading-snug">
              Enkel <em>Standaard BTW</em> is actief tot een geverifieerde regime-catalog voor dit land toegevoegd wordt.
              Er worden geen niet-bevestigde juridische teksten getoond.
            </p>
          </div>
        )}

        <div className="mt-3 space-y-2">
          {(taxRegimes.regimes || []).map((r) => {
            const isPending = r.legal_notice_status === "pending_verification";
            const hint = TAX_REGIME_HINTS[r.key] || "";
            return (
              <div key={r.key}
                className="rounded-xl border border-[#E5E2DC] bg-white p-3 flex items-start gap-3"
                data-testid={`tax-regime-row-${r.key}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[#2C2C2A]">{r.label}</div>
                  {hint && <p className="text-[11px] text-[#6E6E6C] mt-1 leading-snug">{hint}</p>}
                </div>
                {isPending && (
                  <div className="text-[10px] bg-[#FDF7F2] border border-[#F0C4A3] text-[#7F4124] rounded-full px-2 py-0.5 flex-shrink-0 mt-0.5">
                    Wettelijke tekst pending
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {taxRegimes.catalog_available && (taxRegimes.regimes || []).some(r => r.legal_notice_status === "pending_verification") && (
          <p className="text-[11px] text-[#6E6E6C] mt-3 leading-snug">
            De wettelijke tekst voor verlaagd tarief en BTW-verlegd is nog te bevestigen door je accountant.
            Tot dan verschijnt op de PDF een duidelijke "pending"-vermelding.
          </p>
        )}
      </CollapsibleSettingsCard>

      {/* v14i — E-mail-standaardinstellingen (centrale bron sinds v14j). */}
      <CollapsibleSettingsCard
        id="email"
        title="E-mail"
        icon={<Mail className="w-4 h-4 text-[#D96C4A]" />}
        testid="email-section-card"
      >
        <EmailSettingsSection embedded />
      </CollapsibleSettingsCard>

      {/* v14al — AutoMeasure™ (Fase A · Vlaanderen).
          v14am — AutoSave: toggle persisteert direct zonder "Alles opslaan".
          v14at — Verplaatst naar onder "E-mail". Functionaliteit ongewijzigd. */}
      <CollapsibleSettingsCard
        id="automeasure"
        title="AutoMeasure™"
        icon={<MapPin className="w-4 h-4 text-[#D96C4A]" />}
        testid="automeasure-card"
      >
        <div className="space-y-3">
          <p className="text-sm text-[#4F4F4D]">
            AutoMeasure gebruikt beschikbare externe officiële gebouwgegevens
            (Digitaal Vlaanderen — GRB & DHMV) om metingen als startwaarden in
            te vullen bij exterieuropdrachten. Beschikbaarheid en datavintage
            verschillen per locatie. <strong>Automatisch aangevulde metingen
            moeten altijd door u worden gecontroleerd.</strong> U blijft
            eindverantwoordelijk voor de opmetingen die in offertes worden
            gebruikt.
          </p>
          <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="automeasure-toggle-wrapper">
            <input
              type="checkbox"
              className="w-5 h-5 accent-[#D96C4A]"
              checked={!!form.automeasure_enabled}
              disabled={autoMeasureSaving}
              onChange={(e) => toggleAutoMeasure(e.target.checked)}
              data-testid="automeasure-toggle"
            />
            <span className="text-sm font-medium text-[#2C2C2A]">
              AutoMeasure™ inschakelen (Vlaanderen)
            </span>
            {autoMeasureSaving && (
              <span className="text-[11px] text-[#A8A7A0] italic" data-testid="automeasure-saving">Opslaan…</span>
            )}
          </label>
          <p className="text-[11px] text-[#A8A7A0]">
            {form.automeasure_enabled
              ? "Ingeschakeld — bij compatibele exterieurwerken verschijnt een AutoFill-knop per gevelvlak."
              : "Uitgeschakeld — geen AutoFill-knop; alle metingen blijven manueel. Bestaande projecten blijven ongewijzigd wanneer je de toggle in de toekomst uitzet."}
          </p>
          <div className="rounded-lg bg-[#FAF9F6] border border-[#E5E2DC] p-3 text-[11px] text-[#4F4F4D] space-y-1">
            <div><strong>Bronnen (Fase A):</strong></div>
            <div>· Adressenregister (Digitaal Vlaanderen)</div>
            <div>· GRB Basiskaart Vlaanderen — GBG</div>
            <div>· DHMV II DSM/DTM 1m — opname 2013–2015</div>
            <div className="pt-1">Licentie: Gratis open data licentie Vlaanderen v1.02. Broninformatie wordt bij de AutoMeasure-meting bewaard.</div>
          </div>
        </div>
      </CollapsibleSettingsCard>

      {/* v14i — Merken & materialen voorkeuren (Emma gebruikt deze in AI-teksten) */}
      <CollapsibleSettingsCard
        id="materials"
        title="Merken & materialen"
        icon={<BookOpen className="w-4 h-4 text-[#D96C4A]" />}
        testid="materials-card"
      >
        <MaterialPreferencesSection embedded />
      </CollapsibleSettingsCard>

      {/* v6 — Feedback knop (beta) */}
      <div className="emma-card flex items-start gap-3 border border-[#F4D6C4] bg-[#FDF7F2]" data-testid="feedback-card">
        <MessageSquare className="w-5 h-5 text-[#D96C4A] mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-[#2C2C2A]">Feedback geven</div>
          <p className="text-sm text-[#4F4F4D] mt-0.5">
            Stuit je op iets dat beter kan? Open je e-mailprogramma met alle context al ingevuld.
          </p>
        </div>
        <Button
          onClick={openFeedbackMail}
          variant="outline"
          className="rounded-xl h-10 px-4 gap-2 border-[#D96C4A] text-[#D96C4A] hover:bg-[#D96C4A] hover:text-white flex-shrink-0"
          data-testid="feedback-btn"
        >
          <Mail className="w-4 h-4" /> Stuur feedback
        </Button>
      </div>

      {/* v6 F12e — "Testershandleiding (PDF)" vervangen door "Help & handleiding" */}
      <div className="emma-card flex items-start gap-3 border border-[#F4D6C4] bg-[#FDF7F2]" data-testid="manual-card">
        <BookOpen className="w-5 h-5 text-[#D96C4A] mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-[#2C2C2A]">Help & handleiding</div>
          <p className="text-sm text-[#4F4F4D] mt-0.5">
            Snel wegwijs in EmmaMeasure™: eerste offerte, ruimtes meten, werkzones, trap, sjablonen, PDF-instellingen en FAQ.
          </p>
        </div>
        <Link
          to="/help"
          data-testid="manual-open-btn"
          className="rounded-xl h-10 px-4 gap-2 border border-[#D96C4A] text-[#D96C4A] hover:bg-[#D96C4A] hover:text-white flex-shrink-0 inline-flex items-center justify-center text-sm font-medium transition-colors"
        >
          <BookOpen className="w-4 h-4 mr-2" /> Openen
        </Link>
      </div>

      <div className="flex justify-end pt-2 sticky bottom-4 z-10">
        <Button onClick={save} disabled={saving} className="h-12 px-6 rounded-xl bg-[#D96C4A] hover:bg-[#C05A3C] shadow-lg" data-testid="company-save-btn">
          {saving ? "Bezig…" : "Alles opslaan"}
        </Button>
      </div>

      <div className="text-xs text-[#4F4F4D] pt-2">
        EmmaForge™ is de softwareleverancier — uw bedrijf blijft de aannemer op elke offerte.
      </div>
    </div>
  );
}
